import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import React, { useEffect, useReducer, useState } from 'react';
import { useAnalyzerSerialAutocomplete } from '../hooks/useAnalyzerSerialAutocomplete';
import { DialogSize, Form } from '../utils/sharedTypes';
import {
  convertItemToPlural,
  prettyPrintItem,
} from '../utils/stringManipulation';
import useStyles from './itemDialogStyles';

const AddItemDialog = ({
  addModalOpen,
  setAddModalOpen,
  addFormInitialState,
  itemCreator,
  itemValidator,
  formInstructions,
  handlerMiddleware,
  children,
  itemName,
  dialogSize,
}: {
  addModalOpen: boolean;
  setAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addFormInitialState: object;
  itemCreator: Function;
  // Function that evaluates the current form state, returning true only if the item may be added
  itemValidator: (item: any) => boolean;
  // Instructions displayed when itemValidator has returned false
  formInstructions: string;
  // Function called within the form change handlers; the resulting object will be combined with
  //  the name / value pair from the change event, with the results of the handlerMiddleware function
  //  overriding the name / value pair, if applicable. The user of this component is expected to handle any
  //  results from the Autocomplete Material UI component; this component will only handle simple name/value
  //  pairs set as described above.
  handlerMiddleware: (
    name: string,
    value: string,
    state: Form,
    autoboxValue?: object,
    autoboxLabel?: string
  ) => object;
  // Function that returns the add/edit form elements; each form element should be controlled, with
  //   a name prop containing a string that matches the field on the form object that that element
  //   changes; the value of each form element should be set to formState.fieldName, where fieldName
  //   matches the string of the name prop; handler should be keyed to relevant onChange props; enterHandler
  //   should be keyed to any relevant onKeyDown props
  children: (formState: any, handler: any, enterHandler?: any) => JSX.Element;
  // Optional item name that replaces "Item" in dialog header
  itemName?: string;
  // Optional size for dialog; defaults to 'md'
  dialogSize?: DialogSize;
}) => {
  const classes = useStyles();
  const analyzerAutoComplete = useAnalyzerSerialAutocomplete();
  const [itemsToCreate, setItemsToCreate] = useState<any[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [addResults, setAddResults] = useState<string[]>([
    'You must click Save to submit your items',
  ]);
  const [addFormState, setAddFormState] = useReducer(
    (curVals: any, newVals: any) => ({ ...curVals, ...newVals }),
    addFormInitialState
  );
  // If initial state passed to dialog changes, reset the form state
  useEffect(() => {
    setAddFormState(addFormInitialState);
  }, [addFormInitialState]);
  const handleFormChange = (
    event: any,
    autoboxValue?: object,
    autoboxLabel?: string
  ) => {
    const { name, value } = event.target;
    const middlewareResult = handlerMiddleware(
      name,
      value,
      addFormState,
      autoboxValue,
      autoboxLabel
    );
    const autoCompleteResults = analyzerAutoComplete(name, value);
    setAddFormState({
      [name]: value,
      ...autoCompleteResults,
      ...middlewareResult,
    });
  };
  const handleEnter = (event: any) => {
    if (event.key === 'Enter') {
      setItemsToCreate((state) => [...state, addFormState]);
      setAddFormState(addFormInitialState);
    }
  };
  const handleAddModalAdd = (event: any) => {
    setItemsToCreate((state) => [...state, addFormState]);
    setAddFormState(addFormInitialState);
  };
  const handleAddModalClose = () => {
    setAddModalOpen(false);
    setItemsToCreate([]);
    setAddResults(['You must click Save to submit your items']);
    setSubmitted(false);
    setAddFormState(addFormInitialState);
  };
  const handleAddModalSubmit = async () => {
    setAddResults([]);
    for (const item of itemsToCreate) {
      const result = await itemCreator(item);
      if ('error' in result) {
        setAddResults((addResults) => {
          return [
            ...addResults,
            // @ts-ignore
            `Failed to create item. ${result.error.data.message} `,
          ];
        });
      } else {
        setAddResults((addResults) => {
          return [...addResults, `Item created.`];
        });
      }
    }
    setSubmitted(true);
  };
  return (
    <Dialog
      fullWidth
      maxWidth={dialogSize ? dialogSize : 'md'}
      open={addModalOpen}
      aria-labelledby='form-dialog-title'
    >
      <DialogTitle id='form-dialog-title'>
        Add {itemName ? convertItemToPlural(itemName) : 'Item'}
      </DialogTitle>
      <DialogContent>
        {!submitted && children(addFormState, handleFormChange, handleEnter)}
        {!submitted && !itemValidator(addFormState) && (
          <Typography style={{ marginTop: '1rem' }} color='error'>
            {formInstructions}
          </Typography>
        )}
        {itemsToCreate.length > 0 && (
          <>
            <Typography className={classes.listTitle} variant='subtitle1'>
              Items To Create
            </Typography>
            <Divider />
            <List>
              {itemsToCreate.map((item, idx) => (
                <ListItem key={idx}>
                  <Typography>{prettyPrintItem(item)}</Typography>
                </ListItem>
              ))}
            </List>
            <Typography className={classes.listTitle} variant='subtitle1'>
              Results
            </Typography>
            <Divider />
            <List>
              {addResults.map((result, idx) => (
                <ListItem key={idx}>
                  <Typography>{result}</Typography>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      {submitted ? (
        <DialogActions>
          <Button onClick={handleAddModalClose} color='primary'>
            Close
          </Button>
        </DialogActions>
      ) : (
        <DialogActions>
          <Button onClick={handleAddModalClose} color='primary'>
            Cancel
          </Button>
          {itemValidator(addFormState) && (
            <Button onClick={handleAddModalAdd} color='primary'>
              Add
            </Button>
          )}
          {itemsToCreate.length > 0 && (
            <Button onClick={handleAddModalSubmit} color='primary'>
              Save
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AddItemDialog;
